import React, { createContext, useContext, useState, useEffect } from 'react';
import { API_CONFIG, getApiUrl } from '../config/api';
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';

const AppContext = createContext();

export function AppProvider({ children }) {
  const [authToken, setAuthToken] = useState(null);
  const [tokenExpiry, setTokenExpiry] = useState(null);

  const refreshToken = async () => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens.idToken.toString();
      
      // Get token expiration time from JWT
      const payload = JSON.parse(atob(idToken.split('.')[1]));
      const expiryTime = payload.exp * 1000; // Convert to milliseconds
      
      setAuthToken(idToken);
      setTokenExpiry(expiryTime);
      
      return idToken;
    } catch (error) {
      console.error('Error refreshing token:', error);
      setAuthToken(null);
      setTokenExpiry(null);
      return null;
    }
  };

  const getIdToken = async () => {
    const currentTime = Date.now();
    
    // If token doesn't exist or is expired/about to expire (within 5 minutes), refresh it
    if (!authToken || !tokenExpiry || currentTime >= tokenExpiry - 300000) {
      return refreshToken();
    }
    
    return authToken;
  };

  // Refresh token when component mounts
  useEffect(() => {
    refreshToken();
  }, []);

  const fetchWithAuth = async (endpoint, options = {}) => {
    const token = await getIdToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
      Authorization: `Bearer ${token}`,
    };

    const response = await fetch(endpoint, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorBody = await response.json();
      const error = new Error(errorBody.message || "Failed to fetch data");
      error.status = response.status;
      throw error;
    }

    return response.json();
  };

  const getSalons = async () => {
    const responseJson = await fetchWithAuth(
      getApiUrl(API_CONFIG.ENDPOINTS.SALONS)
    );
    return responseJson;
  };

  // Fetch services
  const getServices = async (salonId) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.SERVICES_BY_SALON, '', { salonId })
      );

      return responseJson;
    } catch (error) {
      console.error('Error fetching services:', error);
      throw error;
    }
  }

  // Fetch services
  const createServices = async (servicesData) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.SERVICES),
        {
          method: 'POST',
          body: JSON.stringify(servicesData),
        }
      );

      return responseJson;
    } catch (error) {
      if (error.message && error.message.includes('(Status Code:')) {
        error.message = error.message.replace(/\s*\(Status Code: \d+\)/, '');
      }
      throw error;
    }
  }

  const updateServices = async (servicesData) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.SERVICES),
        {
          method: 'PUT',
          body: JSON.stringify(servicesData),
        }
      );

      return responseJson;
    } catch (error) {
      console.error('Error updating services:', error);
      throw error;
    }
  }

  const deleteSalonService = async (salonServiceId) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.SALON_SERVICES, '', { salonServiceId }),
        {
          method: 'DELETE',
        }
      );

      return responseJson;
    } catch (error) {
      console.error('Error deleting salon service:', error);
      throw error;
    }
  }

  const deleteStaffService = async (staffServiceId) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.STAFF_SERVICES, '', { staffServiceId }),
        {
          method: 'DELETE',
        }
      );

      return responseJson;
    } catch (error) {
      console.error('Error deleting staff service:', error);
      throw error;
    }
  }

  const getAppointment = async (apptId) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.APPOINTMENT, '', { apptId })
      );

      return responseJson;
    } catch (error) {
      console.error('Error fetching appointment:', error);
      throw error;
    }
  }

  // Fetch appointments
  const getAppointments = async (params = '') => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.APPOINTMENTS, params)
      );

      return responseJson;
    } catch (error) {
      console.error('Error fetching appointments:', error);
      throw error;
    }
  };

  // Create appointment
  const createAppointment = async (appointmentData) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.APPOINTMENTS),
        {
          method: 'POST',
          body: JSON.stringify(appointmentData),
        }
      );

      return responseJson;
    } catch (error) {
      console.error('Error creating appointment:', error);
      throw error;
    }
  };

  // Update appointment
  const updateAppointment = async (appointmentData) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.APPOINTMENTS),
        {
          method: 'PUT',
          body: JSON.stringify(appointmentData),
        }
      );
      
      return responseJson;
    } catch (error) {
      console.error('Error updating appointment:', error);
      throw error;
    }
  };

  const createTimeBlock = async (timeBlockData) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.TIME_BLOCKS),
        {
          method: 'POST',
          body: JSON.stringify(timeBlockData),
        }
      );

      return responseJson;
    } catch (error) {
      console.error('Error creating time block:', error);
      throw error;
    }
  }

  const updateTimeBlock = async (timeBlockData) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.TIME_BLOCKS),
        {
          method: 'PUT',
          body: JSON.stringify(timeBlockData),
        }
      );

      return responseJson;
    } catch (error) {
      console.error('Error updating time block:', error);
      throw error;
    }
  }

  const getTimeBlocks = async (salonId, queryParams = '') => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.TIME_BLOCKS_BY_SALON, queryParams, { salonId })
      );

      return responseJson;
    } catch (error) {
      console.error('Error fetching time blocks:', error);
      throw error;
    }
  }

  const deleteTimeBlock = async (timeBlockId, appliesTo) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.DELETE_TIME_BLOCKS, '', { timeBlockId, appliesTo }),
        {
          method: 'DELETE',
        }
      );

      return responseJson;
    } catch (error) {
      console.error('Error deleting time block:', error);
      throw error;
    }
  }


  // Create salon
  const createSalon = async (salonData) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.SALONS),
        {
          method: 'POST',
          body: JSON.stringify(salonData),
        }
      );

      return responseJson;
    } catch (error) {
      console.error('Error creating salon:', error);
      throw error;
    }
  };

  const updateSalon = async (salonData) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.SALONS),
        {
          method: 'PUT',
          body: JSON.stringify(salonData),
        }
      );

      return responseJson;
    } catch (error) {
      console.error('Error updating salon:', error);
      throw error;
    }
  }

  const getSalonStaff = async (salonId) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.SALON_STAFF, '', { salonId })
      );

      return responseJson;
    } catch (error) {
      console.error('Error fetching salon staff:', error);
      throw error;
    }
  }

  const updateStaff = async (employeeData) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.STAFF),
        {
          method: 'PUT',
          body: JSON.stringify(employeeData),
        }
      );
          
      return responseJson;
    } catch (error) {
      console.error('Error updating staff:', error);
      throw error;
    }
  }

  const deleteStaff = async (staffId) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.STAFF_BY_ID, '', { staffId }),
        {
          method: 'DELETE',
        }
      );

      return responseJson;
    } catch (error) {
      console.error('Error deleting staff:', error);
      throw error;
    }
  }

  const inviteStaff = async (employeeData) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.INVITE_STAFF),
        {
          method: 'POST',
          body: JSON.stringify(employeeData),
        }
      );
      
      return responseJson;
    } catch (error) {
      console.error('Error inviting staff:', error);
      throw error;
    }
  }

  const getCheckoutUrl = async (body) => {
    try {
      const response = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.GET_CHECKOUT_URL),
        { method: 'POST', body: JSON.stringify(body) }
      );
      
      return response;
    } catch (error) {
      console.error('Error getting checkout url:', error);
      throw error;
    }
  };

  const createStripeConnectAccount = async (businessInfo, currentUser) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.CREATE_STRIPE_CONNECT_ACCOUNT), 
        {
          method: 'POST',
          body: JSON.stringify({
            salonId: businessInfo.id,
            salonName: businessInfo.salon_name,
            email: currentUser.email,
            phone: `+${businessInfo.country_code}${businessInfo.area_code}${businessInfo.phone_number}`,
            address: businessInfo.street_address,
            city: businessInfo.city,
            state: businessInfo.state,
            zipCode: businessInfo.zip_code
          }),
        }
      );
      
      return responseJson;
    } catch (error) {
      console.error('Error creating stripe connect account:', error);
      throw error;
    }
  }
  
  const getStripeConnectAccountLink = async (connectedAccountId) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.GET_STRIPE_CONNECT_ACCOUNT_LINK), 
        {
          method: 'POST',
          body: JSON.stringify({
            accountId: connectedAccountId,
          }),
        }
      );

      return responseJson;
    } catch (error) {
      console.error('Error getting stripe connect account link:', error);
      throw error;
    }
  }

  const initializeStripeConnectAccountSession = async (connectedAccountId) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.INITIALIZE_STRIPE_CONNECT_ACCOUNT_SESSION),
        {
          method: 'POST',
          body: JSON.stringify({
            accountId: connectedAccountId,
          }),
        }
      );

      return responseJson;
    } catch (error) {
      console.error('Error initializing stripe connect account session:', error);
      throw error;
    }
  }

  const registerStripeConnectTerminalDevice = async (deviceData, businessInfo) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.REGISTER_STRIPE_TERMINAL_DEVICE),
        {
          method: 'POST',
          body: JSON.stringify({
            ...deviceData,
            locationName: businessInfo?.salon_name,
            accountId: businessInfo?.stripe_connect_account_id,
            address: businessInfo.street_address,
            city: businessInfo.city,
            state: businessInfo.state,
            zipCode: businessInfo.zip_code
          }),
        }
      );

      return responseJson;
    } catch (error) {
      console.error('Error registering stripe connect terminal device:', error);
      throw error;
    }
  }

  const getStripeConnectTerminalDevice = async (deviceId, accountId) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.GET_STRIPE_TERMINAL_DEVICE),
        {
          method: 'POST',
          body: JSON.stringify({
            deviceId: deviceId,
            accountId: accountId,
          }),
        }
      );  

      return responseJson;
    } catch (error) {
      console.error('Error getting stripe connect terminal device:', error);
      throw error;
    }
  }

  const createPaymentIntent = async (paymentIntentData) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.CREATE_PAYMENT_INTENT),
        {
          method: 'POST',
          body: JSON.stringify(paymentIntentData),
        }
      );

      return responseJson;
    } catch (error) {
      console.error('Error creating payment intent:', error);
      throw error;
    }
  }

  const updatePaymentIntent = async (paymentIntentData) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.UPDATE_PAYMENT_INTENT),
        {
          method: 'POST',
          body: JSON.stringify(paymentIntentData),
        }
      );

      return responseJson;
    } catch (error) {
      console.error('Error updating payment intent:', error);
      throw error;
    }
  }

  const cancelPaymentIntent = async (paymentIntentData) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.CANCEL_PAYMENT_INTENT),
        {
          method: 'POST', 
          body: JSON.stringify(paymentIntentData),
        }
      );

      return responseJson;
    } catch (error) {
      console.error('Error canceling payment intent:', error);
      throw error;
    }
  }

  const processPayment = async (paymentIntentData) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.PROCESS_PAYMENT),
        {
          method: 'POST', 
          body: JSON.stringify(paymentIntentData),
        }
      );

      return responseJson;
    } catch (error) {
      console.error('Error processing payment:', error);
      throw error;
    }
  }

  const simulatePayment = async (paymentIntentData) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.SIMULATE_PAYMENT),
        {
          method: 'POST',
          body: JSON.stringify(paymentIntentData),
        }
      );  

      return responseJson;
    } catch (error) {
      console.error('Error simulating payment:', error);
      throw error;
    }
  }

  const pollStripePaymentStatus = async (appointmentId) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.POLL_STRIPE_PAYMENT_STATUS),
        {
          method: 'POST',
          body: JSON.stringify({
            appointmentId: appointmentId,
          }),
        }
      );

      return responseJson;
    } catch (error) {
      console.error('Error polling stripe payment status:', error);
      throw error;
    }
  }

  const value = {
    getIdToken,
    fetchWithAuth,
    getSalons,
    getServices,
    createServices,
    updateServices,
    deleteSalonService,
    deleteStaffService,
    getAppointments,
    getAppointment,
    createAppointment,
    updateAppointment,
    createTimeBlock,
    updateTimeBlock,
    getTimeBlocks,
    deleteTimeBlock,
    createSalon,
    updateSalon,
    getSalonStaff,
    updateStaff,
    deleteStaff,
    inviteStaff,
    getCheckoutUrl,
    createStripeConnectAccount,
    getStripeConnectAccountLink,
    initializeStripeConnectAccountSession,
    registerStripeConnectTerminalDevice,
    getStripeConnectTerminalDevice,
    createPaymentIntent,
    updatePaymentIntent,
    cancelPaymentIntent,
    processPayment,
    simulatePayment,
    pollStripePaymentStatus,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};  